import { useState, useRef } from 'react';
import { ReactComponent as DigntecLogo } from '../../Assets/images/DigntecLogo.svg';
import TvIcon from '@mui/icons-material/Tv';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CircularProgress from '@mui/material/CircularProgress';
import { api } from '../../api/api';

// ── 스타일 (인라인 — 별도 CSS 의존성 없음) ────────────────────────────────────
const S = {
  root: {
    minHeight: '100vh',
    display: 'flex',
    fontFamily: "'Inter', 'Pretendard', 'Apple SD Gothic Neo', sans-serif",
    background: '#f8f7ff',
  },
  left: {
    flex: '0 0 42%',
    background: 'linear-gradient(145deg, #2d0b99 0%, #551FFF 45%, #7c4dff 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 56px',
    position: 'relative',
    overflow: 'hidden',
  },
  brand: { position: 'relative', zIndex: 1, maxWidth: 360 },
  brandTitle: {
    fontSize: 32,
    fontWeight: 800,
    color: '#ffffff',
    lineHeight: 1.25,
    margin: '24px 0 14px',
  },
  brandSub: {
    fontSize: 15,
    lineHeight: 1.65,
    color: 'rgba(255,255,255,0.80)',
    margin: 0,
  },
  steps: { marginTop: 36, display: 'flex', flexDirection: 'column', gap: 16 },
  step: { display: 'flex', alignItems: 'flex-start', gap: 14 },
  stepNum: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.18)',
    color: '#fff',
    fontWeight: 700,
    fontSize: 13,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginTop: 1,
  },
  stepText: { color: 'rgba(255,255,255,0.88)', fontSize: 14, lineHeight: 1.55 },
  right: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 32px',
  },
  card: {
    width: '100%',
    maxWidth: 440,
    background: '#fff',
    borderRadius: 16,
    boxShadow: '0 4px 32px rgba(85,31,255,0.10)',
    padding: '40px 40px 44px',
  },
  cardTitle: {
    fontSize: 22,
    fontWeight: 700,
    color: '#1a1a2e',
    margin: '0 0 6px',
  },
  cardSub: { fontSize: 14, color: '#666', margin: '0 0 28px' },
  label: {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    color: '#444',
    marginBottom: 8,
  },
  codeWrap: { display: 'flex', gap: 8, marginBottom: 24 },
  codeInput: {
    flex: 1,
    padding: '14px 16px',
    fontSize: 28,
    fontWeight: 700,
    letterSpacing: '0.18em',
    border: '2px solid #e0d6ff',
    borderRadius: 10,
    outline: 'none',
    textAlign: 'center',
    color: '#1a1a2e',
    background: '#f8f7ff',
    transition: 'border-color 0.2s',
    width: '100%',
  },
  btn: {
    width: '100%',
    padding: '14px',
    background: '#551FFF',
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    fontWeight: 700,
    fontSize: 15,
    cursor: 'pointer',
    transition: 'background 0.18s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  btnDisabled: { background: '#b0a0ff', cursor: 'not-allowed' },
  infoBox: {
    background: '#f3f0ff',
    border: '1.5px solid #c4b5fd',
    borderRadius: 10,
    padding: '16px 18px',
    marginBottom: 20,
  },
  infoRow: { display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 },
  infoLabel: { color: '#7c6fa0', fontWeight: 600 },
  infoVal: { color: '#1a1a2e', fontWeight: 500 },
  badge: (connected) => ({
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 600,
    background: connected ? '#d1fae5' : '#fef3c7',
    color: connected ? '#065f46' : '#92400e',
  }),
  successBox: {
    textAlign: 'center',
    padding: '8px 0 4px',
  },
  successIcon: { color: '#10b981', fontSize: 56, marginBottom: 12 },
  successTitle: { fontSize: 20, fontWeight: 700, color: '#1a1a2e', margin: '0 0 8px' },
  successSub: { fontSize: 14, color: '#555', margin: '0 0 24px', lineHeight: 1.6 },
  errBox: {
    background: '#fff1f2',
    border: '1.5px solid #fca5a5',
    borderRadius: 10,
    padding: '12px 16px',
    fontSize: 13,
    color: '#b91c1c',
    marginBottom: 16,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
  },
  resetBtn: {
    background: 'none',
    border: 'none',
    color: '#551FFF',
    fontWeight: 600,
    fontSize: 14,
    cursor: 'pointer',
    padding: 0,
    textDecoration: 'underline',
  },
};

// ── 단계 상수 ─────────────────────────────────────────────────────────────────
const STEP = { INPUT: 'input', CONFIRM: 'confirm', SUCCESS: 'success' };

export default function Activate() {
  const [step, setStep] = useState(STEP.INPUT);
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deviceInfo, setDeviceInfo] = useState(null);
  const inputRefs = useRef([]);

  const code = digits.join('');
  const codeComplete = code.length === 6 && /^\d{6}$/.test(code);

  // ── 코드 입력 핸들러 ──────────────────────────────────────────────────────
  const handleDigit = (idx, val) => {
    const v = val.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[idx] = v;
    setDigits(next);
    setError('');
    if (v && idx < 5) inputRefs.current[idx + 1]?.focus();
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    }
    if (e.key === 'Enter' && codeComplete) handleLookup();
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setDigits(pasted.split(''));
      setError('');
      inputRefs.current[5]?.focus();
    }
    e.preventDefault();
  };

  // ── Step 1: 코드 조회 ─────────────────────────────────────────────────────
  const handleLookup = async () => {
    if (!codeComplete) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get(`/activate/${code}`);
      if (!data.found) {
        setError('No device found with this code. Make sure the app is running on your TV and try again.');
      } else {
        setDeviceInfo(data);
        setStep(STEP.CONFIRM);
      }
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2: 활성화 ────────────────────────────────────────────────────────
  const handleActivate = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.post('/activate', { code });
      if (data.success) {
        setStep(STEP.SUCCESS);
      } else {
        setError(data.message || 'Activation failed. Please try again.');
      }
    } catch (err) {
      const msg = err?.response?.data?.message || 'Activation failed. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep(STEP.INPUT);
    setDigits(['', '', '', '', '', '']);
    setError('');
    setDeviceInfo(null);
    setTimeout(() => inputRefs.current[0]?.focus(), 50);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={S.root}>
      {/* Left panel */}
      <div style={S.left}>
        <div style={S.brand}>
          <div style={{ marginBottom: 8 }}>
            <DigntecLogo style={{ height: 44, filter: 'brightness(0) invert(1)' }} />
          </div>
          <h1 style={S.brandTitle}>Activate Your Display</h1>
          <p style={S.brandSub}>
            Connect your Fire TV or Android display to the Digntec Signage network in seconds.
          </p>
          <div style={S.steps}>
            {[
              { n: 1, text: 'Install the Digntec Signage app on your TV and open it.' },
              { n: 2, text: 'Note the 6-digit code displayed on screen.' },
              { n: 3, text: 'Enter the code here and click Activate.' },
              { n: 4, text: 'Your display will start showing content within seconds.' },
            ].map(({ n, text }) => (
              <div key={n} style={S.step}>
                <div style={S.stepNum}>{n}</div>
                <p style={{ ...S.stepText, margin: 0 }}>{text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right panel */}
      <div style={S.right}>
        <div style={S.card}>

          {/* ── STEP: INPUT ── */}
          {step === STEP.INPUT && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <TvIcon style={{ color: '#551FFF', fontSize: 28 }} />
                <div>
                  <h2 style={S.cardTitle}>Enter Activation Code</h2>
                  <p style={{ ...S.cardSub, margin: 0 }}>
                    Enter the 6-digit code shown on your TV screen.
                  </p>
                </div>
              </div>

              <label style={S.label}>Activation Code</label>
              <div style={{ display: 'flex', gap: 8, marginBottom: 20 }} onPaste={handlePaste}>
                {digits.map((d, i) => (
                  <input
                    key={i}
                    ref={el => inputRefs.current[i] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={d}
                    onChange={e => handleDigit(i, e.target.value)}
                    onKeyDown={e => handleKeyDown(i, e)}
                    style={{
                      ...S.codeInput,
                      borderColor: d ? '#551FFF' : '#e0d6ff',
                      fontSize: 24,
                    }}
                    autoFocus={i === 0}
                  />
                ))}
              </div>

              {error && (
                <div style={S.errBox}>
                  <ErrorOutlineIcon style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }} />
                  <span>{error}</span>
                </div>
              )}

              <button
                style={{ ...S.btn, ...((!codeComplete || loading) ? S.btnDisabled : {}) }}
                onClick={handleLookup}
                disabled={!codeComplete || loading}
              >
                {loading ? <CircularProgress size={18} style={{ color: '#fff' }} /> : 'Look Up Device'}
              </button>
            </>
          )}

          {/* ── STEP: CONFIRM ── */}
          {step === STEP.CONFIRM && deviceInfo && (
            <>
              <h2 style={S.cardTitle}>Device Found</h2>
              <p style={{ ...S.cardSub, marginBottom: 20 }}>
                We found a device with code <strong>{code}</strong>. Review the details below and click Activate.
              </p>

              <div style={S.infoBox}>
                {[
                  ['Code', code],
                  ['Display Name', deviceInfo.name || '(Not yet named)'],
                  ['Device Model', deviceInfo.deviceModel || 'Fire TV / Android TV'],
                  ['Location', deviceInfo.location || '—'],
                  ['Connection', null, deviceInfo.connected],
                ].map(([label, val, isBool], i) => (
                  <div key={i} style={{ ...S.infoRow, marginBottom: i < 4 ? 6 : 0 }}>
                    <span style={S.infoLabel}>{label}</span>
                    {isBool !== undefined
                      ? <span style={S.badge(isBool)}>{isBool ? 'Connected' : 'Waiting...'}</span>
                      : <span style={S.infoVal}>{val}</span>}
                  </div>
                ))}
              </div>

              {error && (
                <div style={S.errBox}>
                  <ErrorOutlineIcon style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }} />
                  <span>{error}</span>
                </div>
              )}

              <button
                style={{ ...S.btn, ...(loading ? S.btnDisabled : {}) }}
                onClick={handleActivate}
                disabled={loading}
              >
                {loading
                  ? <><CircularProgress size={18} style={{ color: '#fff' }} /> Activating...</>
                  : 'Activate Display'}
              </button>

              <div style={{ textAlign: 'center', marginTop: 14 }}>
                <button style={S.resetBtn} onClick={reset}>Enter a different code</button>
              </div>
            </>
          )}

          {/* ── STEP: SUCCESS ── */}
          {step === STEP.SUCCESS && (
            <div style={S.successBox}>
              <CheckCircleIcon style={S.successIcon} />
              <h2 style={S.successTitle}>Display Activated!</h2>
              <p style={S.successSub}>
                Your display has been successfully activated.
                {deviceInfo?.name ? ` "${deviceInfo.name}" is` : ' It is'} now connected to the Digntec Signage network and will begin showing content shortly.
              </p>
              <button
                style={{ ...S.btn, marginBottom: 12 }}
                onClick={() => window.location.href = '/login'}
              >
                Go to Dashboard
              </button>
              <div style={{ textAlign: 'center' }}>
                <button style={S.resetBtn} onClick={reset}>Activate another display</button>
              </div>
            </div>
          )}
        </div>

        {/* Footer note */}
        <p style={{ position: 'absolute', bottom: 24, fontSize: 12, color: '#aaa', textAlign: 'center' }}>
          Need help? Contact{' '}
          <a href="mailto:support@digntec.com" style={{ color: '#551FFF' }}>support@digntec.com</a>
        </p>
      </div>
    </div>
  );
}
